import {Article} from '@/model/Article';
import {Firestore} from '@/plugins/Firestore';
import {Store} from '@/plugins/Store';
import {EventBus} from '@/utils/EventBus';
import {unlockLocalArticleEditors} from '@/service/aiWaitFallback';

/** OpenAIArticleGenerate answered 409: this article already has a run in progress. */
export function isGenerationInProgressError(error: unknown): boolean {
    const failure = error as {code?: unknown; message?: unknown} | null | undefined;
    return failure?.code === 'functions/already-exists'
        || String(failure?.message || '') === 'generation-in-progress';
}

export type GenerationStartOutcome = 'following' | 'failed';

/** Status a failed start goes back to instead of failed with Retry. */
export type GenerationStartRestore = {
    enhanced: Article['enhanced'];
    aiGenerateStatus: Article['aiGenerateStatus'];
};

/**
 * OpenAIArticleGenerate failed (5xx, network, 403 before the invoker is
 * open). The page had already saved aiGenerateStatus: running, and nothing
 * else ends that run: the article stayed "AI Lucy dopisuje" forever, could
 * not be saved and showed no Retry. It becomes failed with Retry and the
 * editors unlock. A 409 means a run is already going; the page follows it.
 * With `restore` the article goes back to that status instead: a block retry
 * from review ran nothing, and failed would drop the review.
 */
export async function settleFailedGenerationStart(
    article: Article,
    error: unknown,
    followRun: () => Promise<unknown>,
    restore?: GenerationStartRestore,
): Promise<GenerationStartOutcome> {
    if (isGenerationInProgressError(error)) {
        await followRun();
        return 'following';
    }
    if (!restore) {
        Store.commit('setArticleAiFailed', true);
    }
    unlockLocalArticleEditors();
    const write = Firestore.updateArticleGenerationStatus(article, restore || {enhanced: 'pending', aiGenerateStatus: 'failed'});
    EventBus.$emit('parts-generation-failed');
    await write.catch(() => undefined);
    return 'failed';
}

export type GenerationRunStart = 'started' | GenerationStartOutcome;

/**
 * Enhance, re-enhance and Retry write running before they call
 * OpenAIArticleGenerate or OpenAIArticleReGenerate. A call that fails before
 * the server claims the run leaves no failed write and no watchdog, so the
 * article stayed running: no Retry, and saves were refused until the run
 * limit passed. It is settled here like a failed first start. 'following'
 * (409) means a run is already going; the caller follows it as if started.
 * `restore` is passed on to settleFailedGenerationStart.
 */
export async function startGenerationRun(
    article: Article,
    call: () => Promise<unknown>,
    report: (error: unknown) => void,
    restore?: GenerationStartRestore,
): Promise<GenerationRunStart> {
    try {
        await call();
        return 'started';
    } catch (error) {
        if (!isGenerationInProgressError(error)) {
            report(error);
        }
        return settleFailedGenerationStart(article, error, async () => undefined, restore);
    }
}
